import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Components
import { Avatar, Button, Col, Input, Row, TermsAgreement, Text } from 'components';
import { AccountLocked, AppleLogin, GgLogin, LoginAttempts } from 'screens/Login/components';

// Utils
import { firebase, storage } from 'core';
import { Api, checkSpecialChars, CTA, theme, validateEmail } from 'lib';

/*
  The social Button variant positions its brand icon absolutely, so it needs a
  positioned ancestor to anchor to; also force the white pill look on the dark bg.
*/
const SocialButtonWrap = styled.div`
  position: relative;
  width: 100%;
  margin: ${({ marg }) => marg || 0};

  button {
    background-color: #fff;
    border: none;
  }
`;

/*
  Sponsor opt-in, sign-up only. Same `auth.sponsorConsent` copy as the main sign-up form and
  RateItAuth. A native checkbox rather than the shared `Checkbox`, which is sized and coloured for
  the light marketing pages, not this dark background.
*/
const ConsentRow = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  margin: 20px 0 0;
`;

const ConsentBox = styled.input`
  width: 16px;
  height: 16px;
  margin: 1px 0 0;
  flex: none;
  accent-color: ${theme.SECONDARY};
  cursor: pointer;
`;

/* The label is what most people press, so it carries the cursor and the `for` binding. */
const ConsentLabel = styled.label`
  margin-left: 10px;
  font-size: 13px;
  line-height: 18px;
  color: #fff;
  cursor: pointer;
`;

const TextDivider = styled.p`
  position: relative;
  margin: 24px 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  text-transform: lowercase;

  &::before {
    position: absolute;
    content: '';
    left: 0;
    top: 50%;
    background-color: rgba(255, 255, 255, 0.4);
    width: 42%;
    height: 1px;
  }
  &::after {
    position: absolute;
    content: '';
    right: 0;
    top: 50%;
    background-color: rgba(255, 255, 255, 0.4);
    width: 42%;
    height: 1px;
  }
`;

/*
  Signup + login step of the CTA flow. isLogin renders the email/password-only
  variant ("Sign in to your fan profile"); otherwise the full signup form with
  the social options.
*/
const CtaAuth = ({ otherUser, onAuthComplete, isLogin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useSelector((state) => state.appState);

  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailErr, setEmailErr] = useState(null);
  const [usernameErr, setUsernameErr] = useState(null);
  const [passErr, setPassErr] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [accountLockedVisible, setAccountLockedVisible] = useState(false);
  // optional, and deliberately absent from `disabled` below — an unticked box never blocks
  // registration, it is recorded as an explicit "no"
  const [sponsorConsent, setSponsorConsent] = useState(false);
  // set once /users/signUp succeeds, so a retry (e.g. username taken) skips re-signup
  const [createdUser, setCreatedUser] = useState(null);

  // signUp now rejects a taken username before creating the account, so normally
  // no account exists until the username is valid. This only becomes true in the
  // rare race where the handle is claimed between signUp and updateUsername — the
  // retry then needs just a fresh username, so don't let email/password gate it.
  const usernameOnly = !!createdUser;

  // Social auth skips onboarding entirely — drop the user straight on compose
  // after the container swap (see navigateTo handling in User.js)
  const redirectUrl = `/shoutout/${otherUser?.username}/compose`;

  const credentialsMissing = !validateEmail(email) || password.length < 8;
  const disabled = isLogin
    ? credentialsMissing
    : !username.length || (!usernameOnly && credentialsMissing);

  async function handleLogin() {
    // silent so backend field errors (wrong password, user not found, locked)
    // render inline instead of the generic global "something went wrong" toast —
    // the wrong-password message lives in `pwErr`, not `err`, so the global
    // handler can't surface it anyway.
    const res = await Api.post('/users/login', { email, password }, 'login', { silent: true });
    if (!res) return;

    if (res.pwErr) {
      setPassErr(res.pwErr);
      setAttempts(res.loginAttempts);
      passwordInputRef.current.focus();
      return;
    }

    if (res.locked) {
      setAttempts(3);
      return setAccountLockedVisible(true);
    }

    if (res.err) {
      emailInputRef.current.focus();
      return setEmailErr(res.err);
    }

    if (res.migratedUser) {
      return navigate('/activate-account', { state: { email } });
    }

    // no token = an unhandled failure (e.g. banned/forbidden) returned silently;
    // show a generic inline error rather than proceeding with an empty token
    if (!res.token) {
      return setEmailErr(t('error.somethingWentWrong'));
    }

    await storage.setItem('token', res.token);

    firebase.logEvent('web_login_from_cta_share', { method: 'email', type: CTA.SEND_SHOUTOUT });

    onAuthComplete(res.user);
  }

  async function handleContinue() {
    let user = createdUser;

    if (!user) {
      // send username so the backend rejects a taken/invalid handle up front and
      // never creates the account in that case. silent so the field errors render
      // inline (username under its box, email under its box) instead of a toast.
      const res = await Api.post(
        '/users/signUp',
        { name: username, email, password, lang: language, username, sponsorConsent },
        'signUp',
        { silent: true }
      );
      if (!res) return;
      if (res.errs?.some((x) => x.input === 'username') || res.input === 'username') {
        return setUsernameErr(res.errs?.find((x) => x.input === 'username')?.err || res.err);
      }
      if (res.err || res.errs || res.status) {
        return setEmailErr(res.err || res.errs?.[0]?.err || t('error.somethingWentWrong'));
      }

      await storage.setItem('token', res.token);

      firebase.logEvent('web_sign_up_from_cta_share', { method: 'email', type: CTA.SEND_SHOUTOUT });

      user = res.user;
      setCreatedUser(user);
    }

    // complete onboarding inline (same call the Onboarding screen makes) so the
    // "choose a username" screen never shows for this flow.
    // silent so a taken username (thrown 4xx) renders inline under the field
    // instead of firing the global error toast.
    const usernameRes = await Api.post(
      '/users/updateUsername',
      { username, referralCode: '', onboardingStep: 2, type: 'fan' },
      'signUp',
      { silent: true }
    );
    if (!usernameRes) return;
    // 200 with a field-level errs array, or a thrown 4xx surfaced as `err`
    if (usernameRes.errs) {
      return setUsernameErr(usernameRes.errs.find((x) => x.input === 'username')?.err);
    }
    if (usernameRes.err) {
      return setUsernameErr(usernameRes.err);
    }
    // server error (5xx) is returned silently without a message — don't fall
    // through to onAuthComplete as if the username update had succeeded
    if (usernameRes.status) {
      return setUsernameErr(t('error.somethingWentWrong'));
    }

    // merge the chosen username explicitly — the compose route guard checks
    // user.username, so don't rely on updateUsername echoing it back
    onAuthComplete({ ...user, ...usernameRes, username });
  }

  function handleEmailChange(e) {
    setEmail(e.target.value);
    setEmailErr(null);
  }

  function handleUsernameChange(e) {
    const { value } = e.target;
    if (checkSpecialChars(value)) return;

    setUsernameErr(null);
    setUsername(value);
  }

  function handlePasswordChange(e) {
    setPassword(e.target.value);
    setPassErr(null);
  }

  return (
    <>
      {/* top block: identity + form; the terms line is pinned to the bottom of
          the screen by the parent Col's space-between */}
      <Col wid="100%" centerAll noFlex>
        {/* who the shout-out is for */}
        <Row centerAll noFlex>
          <Avatar user={otherUser} showVerified />

          <Col marg="0 0 0 12px" center>
            <Text h2 col="#fff">
              {otherUser?.name}
            </Text>

            <Text c1 med col="#fff">
              @{otherUser?.username}
            </Text>
          </Col>
        </Row>

        <Text h2 col="#fff" center marg="32px 0 0">
          {isLogin ? t('callToAction.signInToFanProfile') : t('callToAction.createFanProfile')}
        </Text>

        <Text h4 col="#fff" center marg="8px 0 0">
          {t('callToAction.toSendYourShoutout')}
        </Text>

        <Col wid="100%" marg="32px 0 0" noFlex>
          {!isLogin && (
            <Input
              wid="100%"
              bg="#fff"
              placeholder={t('common.userName')}
              value={username}
              onChange={handleUsernameChange}
              error={usernameErr}
            />
          )}

          <Input
            inputRef={emailInputRef}
            marg={isLogin ? 0 : '16px 0 0'}
            wid="100%"
            bg="#fff"
            placeholder={t('common.email')}
            value={email}
            onChange={handleEmailChange}
            error={emailErr}
          />

          <Input
            inputRef={passwordInputRef}
            marg="16px 0 0"
            wid="100%"
            bg="#fff"
            password
            placeholder={t('common.password')}
            error={
              passErr ||
              (!!password.length && password.length < 8 && 'Must at least contain 8 characters.')
            }
            value={password}
            onChange={handlePasswordChange}
          />
        </Col>

        {/* sign-up only: /users/login takes no consent, and on the username-retry pass the
            account already exists with the answer recorded */}
        {!isLogin && !usernameOnly && (
          <ConsentRow>
            <ConsentBox
              id="shoutoutSponsorConsent"
              type="checkbox"
              checked={sponsorConsent}
              onChange={(e) => setSponsorConsent(e.target.checked)}
            />
            <ConsentLabel htmlFor="shoutoutSponsorConsent">{t('auth.sponsorConsent')}</ConsentLabel>
          </ConsentRow>
        )}

        {isLogin && <LoginAttempts attempts={attempts} />}

        <TextDivider>{t('common.or')}</TextDivider>

        <Col wid="100%" noFlex>
          <AppleLogin
            redirectUrl={redirectUrl}
            CustomButton={(props) => (
              <SocialButtonWrap>
                <Button apple wid="100%" onClick={props.onClick} text={t('common.continue')} />
              </SocialButtonWrap>
            )}
          />

          <GgLogin
            redirectUrl={redirectUrl}
            CustomButton={(props) => (
              <SocialButtonWrap marg="16px 0 0">
                <Button google wid="100%" onClick={props.onClick} text={t('common.continue')} />
              </SocialButtonWrap>
            )}
          />
        </Col>

        <Button
          big
          wid="100%"
          marg="32px 0 0"
          bg={disabled ? theme.GREY_30 : theme.SECONDARY}
          text={t('common.continue')}
          onClick={disabled ? undefined : isLogin ? handleLogin : handleContinue}
          action={isLogin ? 'login' : 'signUp'}
        />

        <AccountLocked
          visible={accountLockedVisible}
          closeModal={() => setAccountLockedVisible(false)}
        />
      </Col>

      {/* pinned to the bottom of the screen; wrapped in a centered Col so the
          terms text centers instead of inheriting the parent's space-between */}
      <Col wid="100%" centerAll noFlex>
        <TermsAgreement col="#fff" underline />
      </Col>
    </>
  );
};

export default CtaAuth;
